import React, { useEffect, useMemo, useState } from 'react';
import SelectDropdown from 'components/base/select-dropdown';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';
import { BatchesType } from 'models/batches.model';

export type BatchOptionType = {
  id: string;
  name: string;
  batch_no: number;
  quantity: string;
};

type Props = {
  ingredientId: string;
  storeId: string;
  batch_no: number;
  selectedBatchNos?: number[];
  currentIndex?: number;
  onBatchSelect: (batch_no: number, quantity: string) => void;
  onBatchSizeChange?: (batchSize: number) => void;
};

const BatchSelectDropdown = ({
  ingredientId,
  storeId,
  batch_no,
  selectedBatchNos = [],
  currentIndex = -1,
  onBatchSelect,
  onBatchSizeChange
}: Props) => {
  const [batches, setBatches] = useState<BatchesType[]>([]);

  const fetchBatches = async () => {
    if (!ingredientId || !storeId) return;

    try {
      const response = await apiService.get(`ingredients/${ingredientId}/stores/${storeId}`, {
        version: 'v2'
      });
      if (response?.data?.data) {
        const fetchedBatches = response.data.data.kitchen_batches;
        setBatches(fetchedBatches);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const batchOptions = useMemo(() => {
    return batches
      .filter((batch) => {
        const isCurrentSelection = batch.batch_no === batch_no;
        const isAlreadySelected = selectedBatchNos.some(
          (batchNo, idx) => batchNo === batch.batch_no && idx !== currentIndex
        );
        return isCurrentSelection || !isAlreadySelected;
      })
      .map((batch) => ({
        id: batch.id,
        name: `Batch ${batch.batch_no} (${batch.quantity} available)`,
        batch_no: batch.batch_no,
        quantity: batch.quantity
      }));
  }, [batches, selectedBatchNos, currentIndex, batch_no]);

  useEffect(() => {
    fetchBatches();
  }, [ingredientId, storeId]);

  useEffect(() => {
    onBatchSizeChange?.(batchOptions.length);
  }, [batchOptions.length]);

  const selectedBatch = useMemo(
    () => batches.find((b) => b.batch_no === batch_no),
    [batches, batch_no]
  );

  return (
    <div>
      <div
        className={`${
          batchOptions.length === 0 && 'pointer-events-none cursor-no-pointer opacity-50 '
        }`}>
        <SelectDropdown
          hasSearch
          placeholder='Select batch'
          label='Batch'
          value={selectedBatch ? `Batch ${selectedBatch.batch_no}` : ''}
          setValue={(item: any) => {
            onBatchSelect(item.batch_no, item.quantity);
          }}
          options={batchOptions}
        />
      </div>
      {batchOptions.length === 0 && (
        <p className='text-red-5 text-sm mt-2'>Ingredient not available</p>
      )}
    </div>
  );
};

export default BatchSelectDropdown;
